import { withTranslation } from "react-i18next";
import WithRouter from "../../../../components/Common/WithRouter";
import React, { useRef, useState } from "react";
import { request } from "../../../../utils/Request";
import Tooltip from "../../../../components/Common/Display/Tooltip";
import HasAccess from "../../../../utils/HasAccess";
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import StateLessGrid from "../../../../components/Shared/StateLessGrid";
import { Button } from "primereact/button";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import { InputField } from "../../../../components/Shared/InputField";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { includes, isEmpty } from "lodash";
import { Badge } from "primereact/badge";
import AccountFilter from "./AccountFilter";
import AccountForm from "./AccountForm";
import AccountDetail from "./AccountDetail";
import AccountDocument from "./AccountDocument";
import ProviderCredentialDrawer from "./ProviderCredentialDrawer";

const AccountList = (props: any) => {
    const { t, moduleState, toastify } = props;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [operation, setOperation] = useState<any>(searchParams.get('operation') ?? null);
    const [operationId, setOperationId] = useState<any>(searchParams.get('operationId') ?? null);
    const [filterPanel, setFilterPanel] = useState<boolean>(false);
    const [initSearchFormState, setInitSearchFormState] = useState<any>({ search: null });
    const [providerLoginOpen, setProviderLoginOpen] = useState<boolean>(false);
    const [providerLoginSupplierId, setProviderLoginSupplierId] = useState<string | null>(null);
    const [providerLoginProviderSlug, setProviderLoginProviderSlug] = useState<string>('emil_frey');
    const tableRef: any = useRef();

    const openProviderLogin = (supplierId: string, providerSlug: string) => {
        setProviderLoginSupplierId(supplierId);
        setProviderLoginProviderSlug(providerSlug);
        setProviderLoginOpen(true);
    };

    const closeProviderLogin = () => {
        setProviderLoginOpen(false);
        setProviderLoginSupplierId(null);
    };

    const onDelete = async (payloads: any): Promise<void> => {
        try {
            await request({
                url: `admin/suppliers/${ payloads.id }`,
                method: 'DELETE',
                data: { _method: 'DELETE' }
            });

            toastify(t("record deleted", { item: t('role') }), "info");
            onReset();
        } catch (e: any) {
            if ( e.status === 422 ) {
                return toastify(e.data.message, "error");
            }

            toastify(t("server error"), "error");
        }
    };

    const columns = [
        { field: 'name', title: t('name'), sorting: true, },
        { field: 'email', title: t('email'), sorting: true,  },
        { field: 'username', title: t('username'), sorting: true,  },
        { field: 'phone', title: t('phone'), sorting: true,  },
        {
            field: 'status',
            title: t('emailStatus'),
            sorting: false,
            render: ({ is_active }: any) => {
                return (
                    <>
                        { ( is_active ) && <Badge severity="success" value={ t('verified') } /> }
                        { ( !is_active ) && <Badge severity="danger" value={ t('unverified') } /> }
                    </>
                );
            }
        },
        { field: 'identification_number', title: t('identification number') },
        {
            field: 'action',
            title: t('action'),
            sorting: false,
            render: (_row: any) => {
                return (
                    <>
                        <HasAccess hasAllAccess={ ['suppliersupdate'] }>
                            <Tooltip
                                tooltip={ t('edit item') }>
                                <a onClick={ () => onOperationClick('edit', _row.id) }>
                                    <i className="fas fa-edit" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                        <HasAccess hasAllAccess={ ['suppliersupdate'] }>
                            <Tooltip
                                tooltip={ t('update client data') }>
                                <a onClick={ () => onEditDocuments(_row.id) } className={ 'ms-2' }>
                                    <i className="fas fa-landmark" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                        <HasAccess hasAllAccess={ ['suppliersdestroy'] }>
                            <Tooltip
                                tooltip={ t('delete item') }>
                                <a
                                    title={ t('delete item') }
                                    className={ "cursor-pointer ms-2 btn btn-soft-danger waves-effect waves-light btn-xs" }
                                    onClick={ (event: any) => confirmPopup({
                                        target: event.currentTarget,
                                        message: t('do you want to delete this record?'),
                                        icon: 'pi pi-info-circle',
                                        // @ts-ignorer
                                        defaultFocus: 'reject',
                                        acceptClassName: 'p-button-danger',
                                        accept: () => onDelete(_row),
                                        reject: () => {
                                        },
                                    }) }>
                                    <i className="fas fa-trash-alt" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                        <HasAccess hasAllAccess={ ['suppliersview'] }>
                            <Tooltip
                                tooltip={ t('detail of item') }>
                                <a onClick={ () => onOperationClick('detail', _row.id) } className={ 'ms-2' }>
                                    <i className="fas fa-eye" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                        <HasAccess hasAllAccess={ ['suppliersupdate'] }>
                            <Tooltip tooltip={ `${t('connect to emil frey')}` }>
                                <Button
                                    type={ "button" }
                                    size={ "small" }
                                    outlined
                                    className={ "ms-2 p-button-sm" }
                                    style={ {
                                        borderColor: '#2563eb',
                                        color: '#2563eb',
                                        padding: '0.125rem 0.4rem',
                                    } }
                                    onClick={ () => openProviderLogin(_row.id, 'emil_frey') }
                                >
                                    <span style={ { fontWeight: 700 } }>EF</span>
                                </Button>
                            </Tooltip>
                        </HasAccess>
                        <HasAccess hasAllAccess={ ['suppliersupdate'] }>
                            <Tooltip tooltip={ `${t('connect to vehicle logistics')}` }>
                                <Button
                                    type={ "button" }
                                    size={ "small" }
                                    outlined
                                    className={ "ms-2 p-button-sm" }
                                    style={ {
                                        borderColor: '#0f766e',
                                        color: '#0f766e',
                                        padding: '0.125rem 0.4rem',
                                    } }
                                    onClick={ () => openProviderLogin(_row.id, 'vehicle_logistics') }
                                >
                                    <span style={ { fontWeight: 700 } }>VL</span>
                                </Button>
                            </Tooltip>
                        </HasAccess>
                    </>
                );
            }
        },
    ];

    const onOperationClick = (operationToBePerformed: any = 'add', id: any = null) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('operation', operationToBePerformed);
        searchParams.set('operationId', id);

        if ( isEmpty(operationToBePerformed) ) {
            searchParams.delete('operation');
        }

        if ( isEmpty(id) ) {
            searchParams.delete('operationId');
        }

        navigate({
            pathname: location.pathname,
            search: searchParams.toString(),
        }, { replace: true });
        setOperationId(id);
        setOperation(operationToBePerformed);
    };

    const onCloseOperation = () => {
        onOperationClick(null, null);
        onReset();
    };

    const onReset = () => {
        props.setQuery({
            ...moduleState,
            filters: [],
            page: 1,
            search: null
        });
    };

    const onSearch = ({ search }: any) => {
        props.setQuery({
            ...moduleState,
            search: search
        });
    };

    const onFilterClick = () => {
        setFilterPanel(true);
    };

    const onEditDocuments = (id: any) => {
        setOperationId(id);
        setOperation("account-documents");
    };

    return (
        <div className={ "block-viewer" }>
            <div className="block-section">
                <div className="block-header">
                    <span className="block-title">
                        <h5 className={ "mb-0" }>{ t('accounts') }</h5>
                    </span>
                    <div className="block-actions">
                        {
                            operation == 'detail' &&
                            <div>
                                <Button type={ "button" }
                                        tooltip={ t("operation cancel") } className={ "ms-2" }
                                        size={ "small" } severity={ "warning" }
                                        tooltipOptions={ { position: 'top' } } onClick={ onCloseOperation }>
                                    <i className="pi pi-arrow-left me-2" /> { t("cancel") }

                                </Button>
                            </div>
                        }
                    </div>
                </div>
                <div className="block-content">
                    {
                        operation !== 'detail' &&
                        <Row className={ "p-2" }>
                            <Col
                              sm={ 12 } md={ 6 } className={ "mb-2" }>
                                <Formik
                                  enableReinitialize={ true }
                                  initialValues={ initSearchFormState }
                                  onSubmit={ onSearch }
                                >
                                    { (props: FormikProps<any>) => {
                                        return (
                                            <Form onSubmit={ props.handleSubmit }>
                                                <Col sm={ 12 } md={ 8 }>
                                                    <Field component={ InputField } withoutLabel={ true }
                                                           inputGroupIconClass={ 'pi pi-search' }
                                                           name="search"
                                                           formGroupClass={ "mb-0" }
                                                           placeholder={ t("search") } />
                                                </Col>
                                            </Form>
                                        );
                                    } }
                                </Formik>
                            </Col>

                            <Col
                              sm={ 12 } md={ 6 } className={ "text-right mb-2" }>
                                <span className="p-buttonset">
                                    <Button size={ "small" } outlined tooltip={ t('add item') }
                                            tooltipOptions={ { position: 'top' } }
                                            onClick={ () => onOperationClick('add') }>
                                        <i className="pi pi-plus" />
                                        <span className={ "ms-1" }>{ t('add') }</span>
                                    </Button>
                                    <Button icon="pi pi-filter-fill" size={ "small" } outlined tooltip={ t('filter') }
                                            onClick={ onFilterClick }
                                            tooltipOptions={ { position: 'top' } } />
                                    <Button onClick={ onReset } icon="pi pi-refresh" size={ "small" } outlined
                                            tooltip={ t('reset') }
                                            tooltipOptions={ { position: 'top' } } />
                                </span>
                            </Col>

                            <Col sm={ 12 }>
                                <StateLessGrid
                                  size={ "normal" }
                                  url={ '/admin/suppliers?1=1' }
                                  name={ "accountList" }
                                  columns={ columns }
                                  moduleState={ moduleState }
                                  setQuery={ props.setQuery }
                                  ref={ tableRef }
                                    // detailRow={ (data: any) => <DetailRow data={ data } /> }
                                />
                            </Col>
                        </Row>
                    }

                    {
                        ( operation === 'detail' && !isEmpty(operationId) ) &&
                        <AccountDetail
                          setQuery={ props.setQuery }
                          toastify={ props.toastify }
                          dropdowns={ props.dropdowns }
                          params={ { operation, operationId } }
                          onClose={ onCloseOperation }
                        />
                    }

                    {
                        ( includes(['add', 'edit'], operation) ) &&
                        <AccountForm
                          moduleState={ moduleState }
                          setQuery={ props.setQuery }
                          toastify={ props.toastify }
                          dropdowns={ props.dropdowns }
                          params={ { operation, operationId } }
                          operationCancel={ onOperationClick }
                          onClose={ onCloseOperation }
                        />
                    }

                    <AccountDocument
                        moduleState={ moduleState }
                        setQuery={ props.setQuery }
                        toastify={ props.toastify }
                        dropdowns={ props.dropdowns }
                        params={ { operation, operationId } }
                        onClose={ onCloseOperation }
                    />

                    <AccountFilter
                        moduleState={ moduleState }
                        setQuery={ props.setQuery }
                        toastify={ props.toastify }
                        dropdowns={ props.dropdowns }
                        visibility={ filterPanel }
                        onClose={ () => setFilterPanel(false) }
                    />

                    <ProviderCredentialDrawer
                        visible={ providerLoginOpen }
                        supplierId={ providerLoginSupplierId }
                        providerSlug={ providerLoginProviderSlug }
                        onClose={ closeProviderLogin }
                        toastify={ toastify }
                    />

                    <ConfirmPopup />
                </div>
            </div>
        </div>
    );
};

AccountList.propTypes = {
    // t: PropTypes.any,
};

export default withTranslation()(WithRouter(AccountList));
